import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, errMsg } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { RequireAuth } from "@/components/RequireAuth";

const useAdmin = (key, url, params) =>
  useQuery({ queryKey: ["admin", key, params], queryFn: () => api.get(url, { params }).then((r) => r.data), refetchInterval: 30000 });

function Stats() {
  const { fmt } = useCurrency();
  const { data: s } = useAdmin("stats", "/admin/stats");
  const items = s
    ? [["Players", s.users], ["Open bets", s.open_bets], ["Pending vouchers", s.pending_vouchers], ["Pending payouts", s.pending_payouts], ["Total staked", fmt(s.total_staked_usd)],
       ["Paid out", fmt(s.total_paid_usd)], ["Gross revenue", fmt(s.gross_revenue_usd)], ["Wallet liability", fmt(s.wallet_liability_usd)], ["Live events loaded", s.events_loaded]]
    : [];
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
      {items.map(([k, v]) => (
        <div key={k} data-testid={`admin-stat-${k.toLowerCase().replace(/ /g, "-")}`} className="rounded-lg border border-[#1E293B] bg-[#121824] p-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">{k}</div>
          <div className="font-odds text-xl font-bold">{v}</div>
        </div>
      ))}
    </div>
  );
}

function Withdrawals() {
  const qc = useQueryClient();
  const { fmt } = useCurrency();
  const [status, setStatus] = useState("pending");
  const { data } = useAdmin("withdrawals", "/admin/withdrawals", status ? { status } : {});
  const act = async (id, action) => {
    let note = "Approved";
    if (action === "reject") {
      note = window.prompt("Reason for rejection?");
      if (note === null) return;
      note = note || "Rejected";
    } else if (action === "paid") {
      if (!window.confirm("Confirm you have paid this amount into the player's bank account?")) return;
      note = "Paid by EFT";
    }
    try {
      await api.post(`/admin/withdrawals/${id}`, { action, note });
      toast.success(`Payout ${action === "paid" ? "marked as paid" : action + "d"}`);
      qc.invalidateQueries({ queryKey: ["admin"] });
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="space-y-3">
      <div className="flex gap-1">
        {["pending", "approved", "paid", "rejected", ""].map((s) => (
          <button key={s || "all"} data-testid={`admin-wd-filter-${s || "all"}`} onClick={() => setStatus(s)}
            className={`rounded-full px-3 py-1 text-xs font-semibold capitalize ${status === s ? "bg-[#10B981] text-[#0B0E14]" : "bg-[#121824] text-slate-300"}`}>
            {s || "all"}
          </button>
        ))}
      </div>
      {(data || []).length === 0 && <p className="text-sm text-slate-400">No payout requests.</p>}
      {(data || []).map((w) => (
        <div key={w.id} data-testid={`admin-withdrawal-${w.id}`} className="rounded-lg border border-[#1E293B] bg-[#121824] p-4 text-sm">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="font-display text-lg font-bold">{w.amount.toFixed(2)} {w.currency} <span className="text-xs text-slate-500">({fmt(w.amount_usd)})</span></div>
              <div className="text-slate-300">{w.user_name} · {w.phone}</div>
              <div className="mt-1 font-odds text-xs text-slate-400">
                {w.bank_name} · {w.account_type} · Acc {w.account_number} · Branch {w.branch_code} · {w.account_holder}
              </div>
              <div className="text-xs text-slate-500">{new Date(w.created_at).toLocaleString()} · <span className="uppercase">{w.status}</span> {w.admin_note && `· ${w.admin_note}`}</div>
            </div>
            <div className="flex gap-2">
              {w.status === "pending" && <Button size="sm" data-testid={`admin-approve-withdrawal-btn-${w.id}`} onClick={() => act(w.id, "approve")} className="bg-sky-500 text-white hover:bg-sky-600">Approve</Button>}
              {["pending", "approved"].includes(w.status) && (
                <>
                  <Button size="sm" data-testid={`admin-paid-withdrawal-btn-${w.id}`} onClick={() => act(w.id, "paid")} className="bg-[#10B981] text-[#0B0E14] hover:bg-[#059669]">Mark paid</Button>
                  <Button size="sm" variant="outline" data-testid={`admin-reject-withdrawal-btn-${w.id}`} onClick={() => act(w.id, "reject")} className="border-[#EF4444] text-[#EF4444]">Reject</Button>
                </>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function Users() {
  const qc = useQueryClient();
  const { fmt, rate, currency } = useCurrency();
  const { data } = useAdmin("users", "/admin/users");
  const adjust = async (u) => {
    const v = window.prompt(`Adjust ${u.name}'s balance by how much ${currency}? (negative to deduct)`);
    if (!v) return;
    const note = window.prompt("Reason?") || "Manual adjustment";
    try {
      await api.post(`/admin/users/${u.id}/adjust`, { amount_usd: parseFloat(v) / rate, note });
      toast.success("Balance adjusted");
      qc.invalidateQueries({ queryKey: ["admin"] });
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="overflow-x-auto rounded-lg border border-[#1E293B]">
      <table className="w-full text-sm">
        <thead className="bg-[#0E131F] text-left text-xs uppercase tracking-wider text-slate-500">
          <tr><th className="p-3">Name</th><th className="p-3">Cell</th><th className="p-3">Role</th><th className="p-3">Balance</th><th className="p-3">Joined</th><th className="p-3" /></tr>
        </thead>
        <tbody>
          {(data || []).map((u) => (
            <tr key={u.id} data-testid={`admin-user-row-${u.id}`} className="border-t border-[#1E293B]">
              <td className="p-3">{u.name}</td><td className="p-3 font-odds">{u.phone}</td><td className="p-3 capitalize">{u.role}</td>
              <td className="p-3 font-odds">{fmt(u.balance_usd)}</td><td className="p-3 text-xs text-slate-400">{new Date(u.created_at).toLocaleDateString()}</td>
              <td className="p-3"><Button size="sm" variant="outline" data-testid={`admin-adjust-btn-${u.id}`} onClick={() => adjust(u)}>Adjust</Button></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Bets() {
  const qc = useQueryClient();
  const { fmt } = useCurrency();
  const [status, setStatus] = useState("open");
  const { data } = useAdmin("bets", "/admin/bets", status ? { status } : {});
  const run = async (fn, msg) => {
    try {
      const r = await fn();
      toast.success(typeof msg === "function" ? msg(r.data) : msg);
      qc.invalidateQueries({ queryKey: ["admin"] });
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-1">
        {["open", "won", "lost", "void", ""].map((s) => (
          <button key={s || "all"} data-testid={`admin-bets-filter-${s || "all"}`} onClick={() => setStatus(s)}
            className={`rounded-full px-3 py-1 text-xs font-semibold capitalize ${status === s ? "bg-[#10B981] text-[#0B0E14]" : "bg-[#121824] text-slate-300"}`}>
            {s || "all"}
          </button>
        ))}
        <Button size="sm" data-testid="admin-settle-now-button" className="ml-auto bg-[#F59E0B] text-[#0B0E14] hover:bg-[#d97706]"
          onClick={() => run(() => api.post("/admin/settle-now"), (d) => `${d.settled} bet(s) settled from latest results`)}>
          Settle from results now
        </Button>
      </div>
      {(data || []).map((b) => (
        <div key={b.id} data-testid={`admin-bet-${b.id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-[#1E293B] bg-[#121824] p-3 text-sm">
          <div className="min-w-0">
            <div className="font-semibold">{b.type === "multi" ? `Multi (${b.legs.length})` : "Single"} · {b.user_phone} · <span className="uppercase">{b.status}</span></div>
            <div className="truncate text-xs text-slate-400">{b.legs.map((l) => `${l.pick_label} @ ${l.odds} (${l.result})`).join(" | ")}</div>
          </div>
          <div className="flex items-center gap-3">
            <span className="font-odds text-xs">Stake {fmt(b.stake_usd)} → {fmt(b.status === "open" ? b.potential_usd : b.payout_usd)}</span>
            {b.status === "open" && (
              <Button size="sm" variant="outline" data-testid={`admin-void-bet-${b.id}`} onClick={() => run(() => api.post(`/admin/bets/${b.id}/void`), "Bet voided & refunded")}>Void</Button>
            )}
          </div>
        </div>
      ))}
      {(data || []).length === 0 && <p className="text-sm text-slate-400">No bets.</p>}
    </div>
  );
}

function Vouchers() {
  const qc = useQueryClient();
  const [status, setStatus] = useState("pending");
  const { data } = useAdmin("vouchers", "/admin/vouchers", status ? { status } : {});
  const act = async (v, action) => {
    const body = { action };
    if (action === "approve") {
      const amt = window.prompt(`Verified voucher value in ${v.currency}?`, String(v.amount));
      if (amt === null) return;
      const val = parseFloat(amt);
      if (!(val > 0)) return toast.error("Enter a valid voucher value");
      if (val > v.amount * 1.5 && !window.confirm(`${val} is much higher than the claimed ${v.amount}. Approve anyway?`)) return;
      body.verified_amount = val;
    } else {
      const note = window.prompt("Reason for rejection?", "Invalid or already redeemed voucher");
      if (note === null) return;
      body.note = note;
    }
    try {
      await api.post(`/admin/vouchers/${v.id}`, body);
      toast.success(action === "approve" ? "Voucher approved — wallet credited" : "Voucher rejected");
      qc.invalidateQueries({ queryKey: ["admin"] });
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const copy = (pin) => navigator.clipboard?.writeText(pin).then(() => toast.success("PIN copied"));
  return (
    <div className="space-y-3">
      <div className="flex gap-1">
        {["pending", "approved", "rejected", ""].map((s) => (
          <button key={s || "all"} data-testid={`admin-voucher-filter-${s || "all"}`} onClick={() => setStatus(s)}
            className={`rounded-full px-3 py-1 text-xs font-semibold capitalize ${status === s ? "bg-[#10B981] text-[#0B0E14]" : "bg-[#121824] text-slate-300"}`}>
            {s || "all"}
          </button>
        ))}
      </div>
      <p className="text-xs text-slate-500">Redeem each PIN in the reseller's merchant portal, then approve with the confirmed value.</p>
      {(data || []).length === 0 && <p className="text-sm text-slate-400">No voucher deposits.</p>}
      {(data || []).map((v) => (
        <div key={v.id} data-testid={`admin-voucher-${v.id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-[#1E293B] bg-[#121824] p-4 text-sm">
          <div>
            <div className="font-display text-lg font-bold">{v.provider_name} · {(v.verified_amount || v.amount).toFixed(2)} {v.currency}</div>
            <button data-testid={`admin-voucher-pin-${v.id}`} onClick={() => copy(v.pin)} className="font-odds text-sm tracking-widest text-[#F59E0B] hover:underline">{v.pin}</button>
            <div className="text-xs text-slate-500">{v.user_name} · {v.phone} · {new Date(v.created_at).toLocaleString()} · <span className="uppercase">{v.status}</span> {v.admin_note && `· ${v.admin_note}`}</div>
          </div>
          {v.status === "pending" && (
            <div className="flex gap-2">
              <Button size="sm" data-testid={`admin-approve-voucher-btn-${v.id}`} onClick={() => act(v, "approve")} className="bg-[#10B981] text-[#0B0E14] hover:bg-[#059669]">Approve</Button>
              <Button size="sm" variant="outline" data-testid={`admin-reject-voucher-btn-${v.id}`} onClick={() => act(v, "reject")} className="border-[#EF4444] text-[#EF4444]">Reject</Button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function Admin() {
  return (
    <RequireAuth admin>
      <div className="space-y-6">
        <h1 className="font-display text-4xl font-black uppercase tracking-tight sm:text-5xl">Admin</h1>
        <Stats />
        <Tabs defaultValue="vouchers">
          <TabsList className="bg-[#121824]">
            <TabsTrigger value="vouchers" data-testid="admin-tab-vouchers">Voucher deposits</TabsTrigger>
            <TabsTrigger value="payouts" data-testid="admin-tab-payouts">Payouts</TabsTrigger>
            <TabsTrigger value="bets" data-testid="admin-tab-bets">Bets</TabsTrigger>
            <TabsTrigger value="users" data-testid="admin-tab-users">Players</TabsTrigger>
          </TabsList>
          <TabsContent value="vouchers" className="mt-4"><Vouchers /></TabsContent>
          <TabsContent value="payouts" className="mt-4"><Withdrawals /></TabsContent>
          <TabsContent value="bets" className="mt-4"><Bets /></TabsContent>
          <TabsContent value="users" className="mt-4"><Users /></TabsContent>
        </Tabs>
      </div>
    </RequireAuth>
  );
}
